import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Textarea,
} from '@org/ui';
import { accessEndDate, formatPrice, monthsFromDays, type PosterInk } from './helpers';
import { Poster } from './poster';
import {
  P,
  btnGhost,
  btnRed,
  dmSerif,
  eyebrowStyle,
  fineStyle,
  oswald,
} from './theme';

// -- The purchase flow: a bottom sheet over the product page, three short
//    steps. There is no Stripe yet, so step 2 is "ask the teacher to unlock
//    it"; when checkout exists, a `payment` step goes into STEPS and gets
//    its own component like the others. --

type Step = 'summary' | 'request' | 'done';
const STEPS: Step[] = ['summary', 'request', 'done'];

// A journey product's page payload; the base access (journey = null) never
// opens this sheet, so the journey is narrowed here.
type PublicProductRaw = RouterOutputs['products']['getPublic'];
export type PublicProduct = Omit<PublicProductRaw, 'journey'> & { journey: NonNullable<PublicProductRaw['journey']> };

type StepProps = {
  data: PublicProduct;
  ink: PosterInk;
};

const INK_BG: Record<PosterInk, { bg: string; text: string }> = {
  red: { bg: P.maxRed, text: P.white },
  pink: { bg: P.charm, text: P.black },
  black: { bg: P.black, text: P.white },
  cream: { bg: P.white, text: P.black },
};

function useDateLocale() {
  const { i18n } = useTranslation('p2e');
  return i18n.language?.startsWith('pt') ? ptBR : enUS;
}

/** The 44×58 thumbnail of the poster in the order summary. */
function MiniPoster({ data, ink }: StepProps) {
  const look = INK_BG[ink];
  const cover = data.product.coverImageUrl;
  return (
    <div
      aria-hidden
      style={{
        width: 44,
        height: 58,
        borderRadius: 2,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        background: look.bg,
        color: look.text,
        border: ink === 'cream' ? `1px solid ${P.lightBorder}` : undefined,
        fontFamily: oswald,
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        textAlign: 'center',
        lineHeight: 1.1,
        padding: 3,
      }}
    >
      {cover ? (
        <img
          src={cover}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        data.product.name
      )}
    </div>
  );
}

function StepEyebrow({ n, name }: { n: number; name: string }) {
  const { t } = useTranslation('p2e');
  return (
    <div style={eyebrowStyle}>{t('products.sheet.step', { n, total: STEPS.length, name })}</div>
  );
}

function StepTitle({ children }: { children: string }) {
  return (
    <h2
      style={{
        fontFamily: dmSerif,
        fontWeight: 400,
        fontSize: 24,
        lineHeight: 1.1,
        margin: '4px 0 12px',
        color: P.darkText,
      }}
    >
      {children}
    </h2>
  );
}

function Row({ label, value, big = false }: { label: string; value: string; big?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
        padding: '8px 0',
        borderTop: `1px dashed ${P.lightBorder}`,
        fontSize: 14,
        color: P.darkText,
      }}
    >
      <span>{label}</span>
      <b
        style={{
          fontFamily: oswald,
          fontWeight: 600,
          letterSpacing: '0.06em',
          fontSize: big ? 20 : 14,
          textAlign: 'right',
        }}
      >
        {value}
      </b>
    </div>
  );
}

function Actions({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginTop: 14,
      }}
    >
      {children}
    </div>
  );
}

// -- Step 1: Resumo --
function SummaryStep({
  data,
  ink,
  onBack,
  onNext,
}: StepProps & { onBack: () => void; onNext: () => void }) {
  const { t, i18n } = useTranslation('p2e');
  const dateLocale = useDateLocale();
  const { product, journey } = data;
  const months = monthsFromDays(product.defaultDurationDays);
  const endDate = accessEndDate(product.defaultDurationDays);
  const monthsLabel = months == null ? null : t('products.months', { count: months });
  const accessValue =
    months == null || !endDate
      ? t('products.noTimeLimit')
      : t('products.sheet.accessValue', {
          months: monthsLabel,
          date: format(endDate, 'PP', { locale: dateLocale }),
        });
  const priceLabel =
    product.priceCents === 0
      ? t('products.free')
      : formatPrice(product.priceCents, product.currency, i18n.language ?? 'pt');
  const posters = t('products.posters', { count: journey.unitCount });
  const seals = t('products.seals', { count: journey.unitCount });
  const classes = t('products.classes', { count: journey.classCount });

  return (
    <div>
      <StepEyebrow n={1} name={t('products.sheet.summaryName')} />
      <StepTitle>{t('products.sheet.summaryTitle')}</StepTitle>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          border: `1px solid ${P.lightBorder}`,
          borderRadius: 6,
          padding: 10,
          margin: '0 0 12px',
        }}
      >
        <MiniPoster data={data} ink={ink} />
        <div style={{ minWidth: 0 }}>
          <b
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: P.darkText,
              display: 'block',
            }}
          >
            {product.name}
          </b>
          <span style={fineStyle}>
            {[
              journey.levelName ? t('products.levelLabel', { level: journey.levelName }) : null,
              posters,
              classes,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>
      <Row label={t('products.sheet.access')} value={accessValue} />
      <Row
        label={t('products.sheet.includes')}
        value={t('products.sheet.includesValue', { posters, seals })}
      />
      <Row label={t('products.sheet.total')} value={priceLabel} big />
      <p style={{ ...fineStyle, margin: '10px 0 0' }}>
        {months == null
          ? t('products.sheet.summaryNoteNoLimit')
          : t('products.sheet.summaryNote', { months: monthsLabel })}
      </p>
      <Actions>
        <Button type="button" variant="outline" className="products-btn" style={btnGhost} onClick={onBack}>
          {t('products.sheet.back')}
        </Button>
        <Button type="button" variant="red" className="products-btn" style={btnRed} onClick={onNext}>
          {t('products.sheet.next')}
        </Button>
      </Actions>
    </div>
  );
}

// -- Step 2: Pedir a liberação (the "no Stripe yet" payment step) --
function RequestStep({
  note,
  onNoteChange,
  isPending,
  errorMessage,
  onBack,
  onSubmit,
}: {
  note: string;
  onNoteChange: (v: string) => void;
  isPending: boolean;
  errorMessage: string | null;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation('p2e');
  return (
    <div>
      <StepEyebrow n={2} name={t('products.sheet.requestName')} />
      <StepTitle>{t('products.sheet.requestTitle')}</StepTitle>
      <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.55, color: P.darkText }}>
        {t('products.sheet.requestBody')}
      </p>
      <label
        htmlFor="products-request-note"
        style={{ ...fineStyle, display: 'block', marginBottom: 6, color: P.midText }}
      >
        {t('products.sheet.noteLabel')}
      </label>
      <Textarea
        id="products-request-note"
        value={note}
        maxLength={500}
        rows={3}
        placeholder={t('products.sheet.notePlaceholder')}
        onChange={(e) => onNoteChange(e.target.value)}
        style={{ borderColor: P.lightBorder, background: P.cream, fontSize: 14 }}
      />
      {errorMessage && (
        <p role="alert" style={{ ...fineStyle, color: P.maxRed, margin: '8px 0 0' }}>
          {errorMessage}
        </p>
      )}
      <Actions>
        <Button
          type="button"
          variant="outline"
          className="products-btn"
          style={btnGhost}
          onClick={onBack}
          disabled={isPending}
        >
          {t('products.sheet.back')}
        </Button>
        <Button
          type="button"
          variant="red"
          className="products-btn"
          style={btnRed}
          onClick={onSubmit}
          isLoading={isPending}
          loadingText={t('products.sheet.submitting')}
        >
          {t('products.sheet.submit')}
        </Button>
      </Actions>
    </div>
  );
}

// -- Step 3: Pedido enviado / Aguardando liberação --
function DoneStep({ data, ink }: StepProps) {
  const { t } = useTranslation('p2e');
  const navigate = useNavigate();
  const { product, journey } = data;
  return (
    <div style={{ textAlign: 'center', padding: '30px 0 10px' }}>
      <div style={eyebrowStyle}>{t('products.sheet.doneEyebrow')}</div>
      <div
        style={{
          display: 'inline-block',
          border: `4px dashed ${P.gold}`,
          color: P.gold,
          fontFamily: oswald,
          fontWeight: 700,
          fontSize: 28,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          padding: '6px 22px',
          transform: 'rotate(-6deg)',
          margin: '10px 0 18px',
        }}
      >
        {t('products.sheet.doneStamp')}
      </div>
      {/* The big stamp above already says "waiting", so the cover stays clean (as in the mock). */}
      <div style={{ width: 200, margin: '0 auto 18px' }}>
        <Poster
          ink={ink}
          coverImageUrl={product.coverImageUrl}
          headline={product.tagline || product.name}
          topLeft={[journey.levelName, t('products.page.coverTag')].filter(Boolean).join(' · ')}
          footLeft={product.name}
        />
      </div>
      <h2
        style={{
          fontFamily: dmSerif,
          fontWeight: 400,
          fontSize: 30,
          lineHeight: 1.1,
          margin: '0 0 10px',
          color: P.darkText,
          textWrap: 'balance',
        } as React.CSSProperties}
      >
        {t('products.sheet.doneTitle')}
      </h2>
      <p style={{ margin: '0 auto', color: P.midText, maxWidth: '60ch', fontSize: 15, lineHeight: 1.55 }}>
        {t('products.sheet.doneBody')}
      </p>
      <Actions>
        <Button
          type="button"
          variant="red"
          className="products-btn"
          style={btnRed}
          onClick={() => navigate({ to: '/mural' })}
        >
          {t('products.sheet.seeMural')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="products-btn"
          style={btnGhost}
          onClick={() => navigate({ to: '/products' })}
        >
          {t('products.sheet.backToStore')}
        </Button>
      </Actions>
    </div>
  );
}

function StepsBar({ current }: { current: number }) {
  return (
    <div aria-hidden style={{ display: 'flex', gap: 6, margin: '0 0 16px' }}>
      {STEPS.map((s, i) => (
        <i
          key={s}
          style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: i <= current ? P.maxRed : P.lightBorder,
          }}
        />
      ))}
    </div>
  );
}

export function PurchaseSheet({
  open,
  onOpenChange,
  data,
  ink,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: PublicProduct;
  ink: PosterInk;
}) {
  const { t } = useTranslation('p2e');
  const utils = trpc.useUtils();
  const [step, setStep] = useState<Step>('summary');
  const [note, setNote] = useState('');

  // Every opening starts at the summary again.
  useEffect(() => {
    if (open) {
      setStep('summary');
      setNote('');
    }
  }, [open]);

  const request = trpc.journeyAccess.requestAccess.useMutation({
    onSuccess: () => {
      setStep('done');
      void utils.products.getPublic.invalidate();
      void utils.products.listActive.invalidate();
      void utils.progress.getStudentMural.invalidate();
    },
  });

  const current = STEPS.indexOf(step);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="products-sheet p-0 border-0 bg-transparent shadow-none"
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
      >
        <SheetTitle className="sr-only">{t('products.sheet.summaryTitle')}</SheetTitle>
        <SheetDescription className="sr-only">{data.product.name}</SheetDescription>
        <div
          style={{
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            borderRadius: '14px 14px 0 0',
            boxShadow: '0 -6px 30px rgba(45,26,31,.12)',
            padding: '14px 18px calc(22px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div
            aria-hidden
            style={{
              width: 44,
              height: 5,
              borderRadius: 3,
              background: P.lightBorder,
              margin: '0 auto 12px',
            }}
          />
          <StepsBar current={current} />
          {step === 'summary' && (
            <SummaryStep
              data={data}
              ink={ink}
              onBack={() => onOpenChange(false)}
              onNext={() => setStep('request')}
            />
          )}
          {step === 'request' && (
            <RequestStep
              note={note}
              onNoteChange={setNote}
              isPending={request.isPending}
              errorMessage={request.error ? t('products.sheet.error') : null}
              onBack={() => setStep('summary')}
              onSubmit={() =>
                request.mutate({
                  productId: data.product.productId,
                  note: note.trim() || undefined,
                })
              }
            />
          )}
          {step === 'done' && <DoneStep data={data} ink={ink} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
